/* ==========================================================================
   AUREO — Golden Hour
   Vanilla web components. No dependencies, no framework, deferred.
   Money is always formatted by Liquid and re-rendered via the Section
   Rendering API, so there is no client-side currency formatting to drift.
   ========================================================================== */
(function () {
  'use strict';

  var A = window.AUREO || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- helpers ----------------------------------------------------------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function toast(message) {
    var el = document.getElementById('Toast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('is-visible'); }, 2600);
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  /* --- Drawer: open/close, focus trap, scroll lock ----------------------- */
  var Drawer = {
    open: function (el, opener) {
      if (!el) return;
      el._opener = opener || document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      var first = $(FOCUSABLE, el);
      if (first) setTimeout(function () { first.focus(); }, 60);
      el._trap = function (e) {
        if (e.key === 'Escape') { Drawer.close(el); return; }
        if (e.key !== 'Tab') return;
        var items = $$(FOCUSABLE, el).filter(function (n) { return n.offsetParent !== null; });
        if (!items.length) return;
        var f = items[0], l = items[items.length - 1];
        if (e.shiftKey && document.activeElement === f) { e.preventDefault(); l.focus(); }
        else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
      };
      document.addEventListener('keydown', el._trap);
    },
    close: function (el) {
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', el._trap);
      if (!$$('.drawer.is-open, .mobile-nav.is-open').length) {
        document.body.classList.remove('no-scroll');
      }
      if (el._opener && el._opener.focus) el._opener.focus();
    }
  };
  window.AureoDrawer = Drawer;

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) {
      var target = document.getElementById(openBtn.getAttribute('data-drawer-open'));
      if (target) { e.preventDefault(); Drawer.open(target, openBtn); }
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__scrim')) {
      var d = e.target.closest('.drawer');
      if (d) Drawer.close(d);
    }
  });

  /* --- Cart -------------------------------------------------------------- */
  var Cart = {
    /** Sections we re-render from the server after every cart mutation. */
    sections: function () {
      var list = ['cart-drawer'];
      var main = document.getElementById('MainCartSection');
      if (main) list.push(main.dataset.sectionId);
      return list.join(',');
    },

    request: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      });
    },

    add: function (id, quantity, properties) {
      var payload = {
        items: [{ id: Number(id), quantity: quantity || 1, properties: properties || undefined }],
        sections: Cart.sections()
      };
      return Cart.request(A.routes.cart_add, payload).then(function (data) {
        Cart.render(data.sections);
        Cart.refreshCount();
        document.dispatchEvent(new CustomEvent('aureo:cart:added', { detail: data }));
        return data;
      });
    },

    change: function (line, quantity) {
      return Cart.request(A.routes.cart_change, {
        line: line,
        quantity: quantity,
        sections: Cart.sections()
      }).then(function (data) {
        Cart.render(data.sections);
        Cart.setCount(data.item_count);
        document.dispatchEvent(new CustomEvent('aureo:cart:updated', { detail: data }));
        return data;
      });
    },

    render: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (key) {
        var html = new DOMParser().parseFromString(sections[key], 'text/html');
        var incoming = html.querySelector('[data-section-root="' + key + '"]');
        var current = document.querySelector('[data-section-root="' + key + '"]');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
      });
    },

    refreshCount: function () {
      return fetch(A.routes.cart + '.js')
        .then(function (r) { return r.json(); })
        .then(function (c) { Cart.setCount(c.item_count); return c; });
    },

    setCount: function (n) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = n;
        el.hidden = !n;
      });
    },

    openDrawer: function () {
      var d = document.getElementById('CartDrawer');
      if (A.cartDrawer && d) Drawer.open(d);
    }
  };
  window.AureoCart = Cart;

  /* Cart line quantity + remove, delegated so re-rendered markup keeps working */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-line]');
    if (!btn) return;
    e.preventDefault();
    var line = btn.getAttribute('data-line');
    var qty = btn.getAttribute('data-qty');
    var wrapper = btn.closest('.drawer__body') || btn.closest('[data-section-root]');
    if (wrapper) wrapper.classList.add('is-loading');
    Cart.change(Number(line), Number(qty)).catch(function () {
      toast(A.strings.error);
    }).finally(function () {
      if (wrapper) wrapper.classList.remove('is-loading');
    });
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    var v = Math.max(0, parseInt(input.value, 10) || 0);
    Cart.change(Number(input.getAttribute('data-line')), v).catch(function () { toast(A.strings.error); });
  });

  /* --- Quantity stepper (generic) ---------------------------------------- */
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-qty-step]');
    if (!b) return;
    var input = $('input', b.parentElement);
    if (!input) return;
    var step = Number(b.getAttribute('data-qty-step'));
    var min = Number(input.min || 1);
    var next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --- Product form (add to cart) ---------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('.js-product-form');
    if (!form) return;
    e.preventDefault();

    var idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) {
      var picker = document.querySelector('variant-picker');
      if (picker) picker.showError(A.strings.selectSize);
      return;
    }

    var btn = form.querySelector('[data-atc]');
    var label = btn ? btn.querySelector('[data-atc-label]') : null;
    var original = label ? label.textContent : '';
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    if (label) label.textContent = '···';

    var qtyInput = form.querySelector('[name="quantity"]');
    Cart.add(idInput.value, qtyInput ? Number(qtyInput.value) : 1)
      .then(function () {
        toast(A.strings.added);
        Cart.openDrawer();
      })
      .catch(function (err) {
        toast((err && err.description) || A.strings.error);
      })
      .finally(function () {
        if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        if (label) label.textContent = original;
      });
  });

  /* --- <variant-picker> --------------------------------------------------
     Reads a Liquid-generated variant map (including pre-rendered price HTML)
     so price, availability and media stay exact without client-side money
     formatting.
     ---------------------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(this.querySelector('[data-variant-data]').textContent);
      } catch (err) {
        this.variants = [];
      }
      this.form = document.getElementById(this.dataset.formId);
      this.errorEl = this.querySelector('[data-option-error]');
      this.addEventListener('change', this.onChange.bind(this));
      this.update();
    }

    get selectedValues() {
      return $$('input[type="radio"]:checked', this).map(function (i) { return i.value; });
    }

    onChange() {
      this.hideError();
      this.update();
    }

    findVariant(values) {
      return this.variants.find(function (v) {
        return v.options.length === values.length && v.options.every(function (o, i) { return o === values[i]; });
      });
    }

    update() {
      var values = this.selectedValues;
      this.markAvailability(values);

      var variant = this.findVariant(values);
      this.current = variant;

      var idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      if (idInput) idInput.value = variant ? variant.id : '';

      // Price
      var priceEl = document.querySelector('[data-price-target]');
      if (priceEl && variant) priceEl.innerHTML = variant.price_html;

      // Inventory note
      var invEl = document.querySelector('[data-inventory-note]');
      if (invEl) {
        if (variant && variant.low_stock_html) { invEl.innerHTML = variant.low_stock_html; invEl.hidden = false; }
        else { invEl.hidden = true; }
      }

      // Add-to-cart state
      var btn = this.form ? this.form.querySelector('[data-atc]') : null;
      var label = btn ? btn.querySelector('[data-atc-label]') : null;
      if (btn) {
        var buyable = !!(variant && variant.available);
        btn.disabled = !buyable;
        if (label) {
          label.textContent = !variant ? A.strings.soldOut : (variant.available ? A.strings.addToCart : A.strings.soldOut);
        }
      }

      // Notify-me for sold-out variants
      var notify = document.querySelector('[data-notify-block]');
      if (notify) notify.hidden = !(variant && !variant.available);

      // Sticky bar
      document.dispatchEvent(new CustomEvent('aureo:variant:change', { detail: { variant: variant } }));

      // Jump gallery to the variant's image
      if (variant && variant.media_id) {
        var gallery = document.querySelector('product-gallery');
        if (gallery) gallery.showMedia(variant.media_id);
      }

      // Keep the URL shareable/back-button correct
      if (variant && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /** Grey out (never hide) combinations that don't exist or are sold out. */
    markAvailability(values) {
      var self = this;
      $$('[data-option-position]', this).forEach(function (group) {
        var pos = Number(group.dataset.optionPosition) - 1;
        $$('input[type="radio"]', group).forEach(function (input) {
          var candidate = values.slice();
          candidate[pos] = input.value;
          var match = self.variants.find(function (v) {
            return v.options.every(function (o, i) {
              return i === pos ? o === input.value : (candidate[i] === undefined || o === candidate[i]);
            });
          });
          var wrap = input.closest('.opt__value');
          if (!wrap) return;
          var unavailable = !match || !match.available;
          wrap.classList.toggle('is-unavailable', unavailable);
          wrap.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        });
      });
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }
    hideError() { if (this.errorEl) this.errorEl.hidden = true; }
  }
  customElements.define('variant-picker', VariantPicker);

  /* --- <product-gallery> -------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.dots = $$('[data-gallery-dot]', this);
      if (!this.track) return;

      this.track.addEventListener('scroll', debounce(this.syncDots.bind(this), 90), { passive: true });
      this.dots.forEach(function (dot, i) {
        on(dot, 'click', function () { this.goTo(i); }.bind(this));
      }.bind(this));

      $$('[data-zoom]', this).forEach(function (btn) {
        on(btn, 'click', function () { this.openLightbox(); }.bind(this));
      }.bind(this));
    }

    get items() { return $$('[data-media-id]', this.track); }

    goTo(i) {
      var item = this.items[i];
      if (item) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    showMedia(mediaId) {
      var idx = this.items.findIndex(function (n) { return n.dataset.mediaId === String(mediaId); });
      if (idx > -1) {
        if (window.innerWidth >= 1000) {
          this.items[idx].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
        } else {
          this.goTo(idx);
        }
      }
    }

    syncDots() {
      var self = this;
      var mid = this.track.scrollLeft + this.track.clientWidth / 2;
      var active = 0;
      this.items.forEach(function (item, i) {
        var left = item.offsetLeft - self.track.offsetLeft;
        if (mid >= left && mid < left + item.offsetWidth) active = i;
      });
      this.dots.forEach(function (d, i) { d.classList.toggle('is-active', i === active); });
    }

    openLightbox() {
      var box = document.getElementById('Lightbox');
      if (!box) return;
      var current = this.items[this.dots.findIndex(function (d) { return d.classList.contains('is-active'); })] || this.items[0];
      var img = current ? current.querySelector('img') : null;
      var target = box.querySelector('img');
      if (img && target) {
        target.src = img.dataset.zoomSrc || img.currentSrc || img.src;
        target.alt = img.alt;
      }
      box.classList.add('is-open');
      document.body.classList.add('no-scroll');
    }
  }
  customElements.define('product-gallery', ProductGallery);

  on(document.getElementById('Lightbox'), 'click', function () {
    this.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  });

  /* --- <sticky-atc> ------------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      var anchor = document.getElementById(this.dataset.anchor);
      if (!anchor) return;
      var self = this;

      // Desktop reveals only after the shopper has scrolled past the buy block;
      // mobile shows it as soon as the main button leaves the viewport.
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { rootMargin: '0px 0px -40px 0px' }).observe(anchor);

      on(document, 'aureo:variant:change', function (e) {
        var v = e.detail.variant;
        var meta = self.querySelector('[data-sticky-meta]');
        var price = self.querySelector('[data-sticky-price]');
        var btn = self.querySelector('[data-sticky-atc]');
        if (meta) meta.textContent = v ? v.title : '';
        if (price && v) price.innerHTML = v.price_html;
        if (btn) btn.disabled = !(v && v.available);
      });

      on(this.querySelector('[data-sticky-atc]'), 'click', function () {
        var form = document.querySelector('.js-product-form');
        if (!form) return;
        var id = form.querySelector('[name="id"]');
        if (!id || !id.value) {
          var picker = document.querySelector('variant-picker');
          if (picker) picker.showError(A.strings.selectSize);
          return;
        }
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* --- <quick-add> --------------------------------------------------------
     One variant → add straight to bag. Multiple → open a size bottom-sheet.
     ---------------------------------------------------------------------- */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      var self = this;
      on(this.querySelector('[data-quick-trigger]'), 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var single = self.dataset.variantId;
        if (single) {
          self.add(single, e.currentTarget);
        } else {
          var sheet = document.getElementById('QuickAddSheet');
          if (!sheet) return;
          sheet.querySelector('[data-quick-body]').innerHTML = self.querySelector('[data-quick-options]').innerHTML;
          sheet.querySelector('[data-quick-title]').textContent = self.dataset.title || '';
          Drawer.open(sheet, e.currentTarget);
        }
      });

      on(this, 'click', function (e) {
        var opt = e.target.closest('[data-quick-variant]');
        if (!opt) return;
        e.preventDefault();
        e.stopPropagation();
        self.add(opt.dataset.quickVariant, opt);
      });
    }

    add(id, btn) {
      if (btn) btn.classList.add('is-loading');
      Cart.add(id, 1)
        .then(function () { toast(A.strings.added); Cart.openDrawer(); })
        .catch(function (err) { toast((err && err.description) || A.strings.error); })
        .finally(function () { if (btn) btn.classList.remove('is-loading'); });
    }
  }
  customElements.define('quick-add', QuickAdd);

  /* Any other [data-quick-variant] button — quick-add sheet, cart upsells.
     <quick-add> stops propagation, so its own buttons never reach here. */
  document.addEventListener('click', function (e) {
    var opt = e.target.closest('[data-quick-variant]');
    if (!opt || opt.hasAttribute('disabled')) return;
    e.preventDefault();
    opt.classList.add('is-loading');
    Cart.add(opt.dataset.quickVariant, 1)
      .then(function () {
        var sheet = document.getElementById('QuickAddSheet');
        if (sheet && sheet.contains(opt)) Drawer.close(sheet);
        toast(A.strings.added);
        Cart.openDrawer();
      })
      .catch(function (err) { toast((err && err.description) || A.strings.error); })
      .finally(function () { opt.classList.remove('is-loading'); });
  });

  /* --- <hero-video> -------------------------------------------------------
     Attaches the video source only after first paint, and only where the
     merchant wants it. The <img> underneath stays the LCP element.
     ---------------------------------------------------------------------- */
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var allowMobile = this.dataset.playOnMobile === 'true';
      // Respect Data Saver and reduced motion — never autoplay film at them.
      var saveData = navigator.connection && navigator.connection.saveData;
      if (reduceMotion || saveData || (isMobile && !allowMobile)) return;

      var self = this;
      var start = function () {
        var video = self.querySelector('video[data-src]');
        if (video) {
          video.src = video.dataset.src;
          video.removeAttribute('data-src');
          video.load();
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
          return;
        }
        if (self.dataset.external) {
          var id = self.dataset.external;
          var type = self.dataset.externalType;
          var src = type === 'vimeo'
            ? 'https://player.vimeo.com/video/' + id + '?background=1&autoplay=1&loop=1&muted=1'
            : 'https://www.youtube.com/embed/' + id + '?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&playlist=' + id;
          var frame = document.createElement('iframe');
          frame.src = src;
          frame.title = 'Background video';
          frame.setAttribute('allow', 'autoplay; encrypted-media');
          frame.setAttribute('tabindex', '-1');
          frame.setAttribute('aria-hidden', 'true');
          frame.loading = 'lazy';
          self.appendChild(frame);
        }
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(start, { timeout: 2200 });
      } else {
        setTimeout(start, 900);
      }
    }
  }
  customElements.define('hero-video', HeroVideo);

  /* --- <media-carousel> --------------------------------------------------- */
  class MediaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      var self = this;

      on(this.prev, 'click', function () { self.scrollBy(-1); });
      on(this.next, 'click', function () { self.scrollBy(1); });
      this.track.addEventListener('scroll', debounce(this.updateNav.bind(this), 90), { passive: true });
      this.updateNav();

      // Pause offscreen videos — saves battery and bandwidth on mobile.
      var vids = $$('video', this);
      if (vids.length && !reduceMotion) {
        var vio = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var p = entry.target.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              entry.target.pause();
            }
          });
        }, { threshold: 0.25 });
        vids.forEach(function (v) { vio.observe(v); });
      }
    }

    scrollBy(dir) {
      var slide = this.track.firstElementChild;
      var w = slide ? slide.offsetWidth + 16 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: dir * w, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    updateNav() {
      if (!this.prev || !this.next) return;
      var max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
    }
  }
  customElements.define('media-carousel', MediaCarousel);

  /* --- <announcement-bar> ------------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      var key = 'aureo:announce:' + (this.dataset.version || '1');
      if (this.dataset.dismissible === 'true' && sessionStorage.getItem(key)) {
        this.remove();
        return;
      }
      var slides = $$('[data-announce-slide]', this);
      if (slides.length > 1) {
        var i = 0;
        var speed = Number(this.dataset.speed || 5) * 1000;
        this._int = setInterval(function () {
          slides[i].classList.remove('is-active');
          i = (i + 1) % slides.length;
          slides[i].classList.add('is-active');
        }, speed);
      }
      var self = this;
      on(this.querySelector('[data-announce-close]'), 'click', function () {
        sessionStorage.setItem(key, '1');
        clearInterval(self._int);
        self.remove();
        measureHeader();
      });
    }
    disconnectedCallback() { clearInterval(this._int); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* --- Header: over-hero state + height variable -------------------------- */
  var header = $('[data-header]');

  function measureHeader() {
    if (!header) return;
    document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
  }

  function initHeader() {
    if (!header) return;
    measureHeader();
    window.addEventListener('resize', debounce(measureHeader, 150));

    var hero = $('[data-hero-transparent]');
    if (!hero) return;
    header.classList.add('is-over-hero');
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        header.classList.toggle('is-over-hero', entry.isIntersecting);
      });
    }, { rootMargin: '-70px 0px 0px 0px', threshold: 0 }).observe(hero);
  }

  /* Mobile nav */
  var mobileNav = $('[data-mobile-nav]');
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-mobile-nav-open]')) {
      if (!mobileNav) return;
      mobileNav.classList.add('is-open');
      document.body.classList.add('no-scroll');
      var f = $(FOCUSABLE, mobileNav);
      if (f) setTimeout(function () { f.focus(); }, 80);
    }
    if (e.target.closest('[data-mobile-nav-close]')) {
      if (!mobileNav) return;
      mobileNav.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && mobileNav && mobileNav.classList.contains('is-open')) {
      mobileNav.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }
  });

  /* --- Scroll reveal ------------------------------------------------------ */
  function initReveal() {
    if (!document.documentElement.classList.contains('motion')) return;
    var els = $$('[data-reveal]');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* --- Facets: filter/sort without a full page load ----------------------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (!this.form) return;

      // Listen on document so the sort <select> — which lives outside this
      // element and is bound via form="FacetForm" — is picked up too, and so
      // the handler survives the form being re-rendered.
      var submit = debounce(function () { self.submit(); }, 250);
      document.addEventListener('change', function (e) {
        if (e.target.form && e.target.form === self.form) submit();
      });

      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-facet-clear]')) return;
        e.preventDefault();
        if (!self.form) return;
        $$('input', self.form).forEach(function (i) {
          if (i.type === 'checkbox') i.checked = false;
          else if (i.type === 'number') i.value = '';
        });
        self.submit();
      });

      window.addEventListener('popstate', function () { self.fetchAndRender(window.location.search); });
    }

    /** Re-queried every time — the form node is replaced on each render. */
    get form() { return document.getElementById('FacetForm'); }

    submit() {
      if (!this.form) return;
      var params = new URLSearchParams(new FormData(this.form));
      // Drop empties so the URL stays clean and shareable
      var clean = new URLSearchParams();
      params.forEach(function (v, k) { if (v) clean.append(k, v); });
      var search = clean.toString() ? '?' + clean.toString() : '';
      window.history.pushState({}, '', window.location.pathname + search);
      this.fetchAndRender(search);
    }

    fetchAndRender(search) {
      var results = document.getElementById('CollectionResults');
      if (results) results.classList.add('is-loading');
      var sectionId = this.dataset.sectionId;
      fetch(window.location.pathname + search + (search ? '&' : '?') + 'section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['CollectionResults', 'FacetFiltersForm', 'FacetBar'].forEach(function (id) {
            var incoming = doc.getElementById(id);
            var current = document.getElementById(id);
            if (incoming && current) current.innerHTML = incoming.innerHTML;
          });
          initReveal();
        })
        .catch(function () { window.location.search = search; })
        .finally(function () { if (results) results.classList.remove('is-loading'); });
    }
  }
  customElements.define('facet-form', FacetForm);

  /* --- Recently viewed ---------------------------------------------------- */
  var RV_KEY = 'aureo:recently-viewed';

  function trackRecentlyViewed() {
    var el = $('[data-track-product]');
    if (!el) return;
    var handle = el.dataset.trackProduct;
    try {
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (err) { /* private mode — silently skip */ }
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      var handles;
      try { handles = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (err) { handles = []; }
      var exclude = this.dataset.exclude;
      handles = handles.filter(function (h) { return h && h !== exclude; }).slice(0, Number(this.dataset.limit || 4));
      if (!handles.length) { this.hidden = true; return; }

      var query = handles.map(function (h) { return 'handle:' + h; }).join(' OR ');
      var url = this.dataset.url + '?q=' + encodeURIComponent(query) + '&resources[type]=product&section_id=' + this.dataset.sectionId;
      var self = this;
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var grid = doc.querySelector('[data-rv-grid]');
          var target = self.querySelector('[data-rv-grid]');
          if (grid && target && grid.children.length) {
            target.innerHTML = grid.innerHTML;
            self.hidden = false;
          } else {
            self.hidden = true;
          }
        })
        .catch(function () { self.hidden = true; });
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* --- Marquee: duplicate content so the loop is seamless ----------------- */
  function initMarquees() {
    $$('[data-marquee]').forEach(function (track) {
      if (track.dataset.cloned) return;
      track.innerHTML += track.innerHTML;
      track.dataset.cloned = 'true';
    });
  }

  /* --- Init --------------------------------------------------------------- */
  function init() {
    initHeader();
    initReveal();
    initMarquees();
    trackRecentlyViewed();
    Cart.refreshCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-init after section reloads
  document.addEventListener('shopify:section:load', function () {
    initReveal();
    initMarquees();
    measureHeader();
  });
})();
